import { useEffect, useState } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Plus, ScanLine, Send, UserPlus, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import SetupWizard from "@/components/SetupWizard";
import DateNav, { todayISO, formatDateLabel } from "@/components/DateNav";
import ReferAcademyModal from "@/components/ReferAcademyModal";

function ReferAcademyBanner() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="card flex flex-col md:flex-row items-center justify-between gap-3 p-5 px-7" data-testid="refer-academy-banner"
      style={{ background: "linear-gradient(90deg, rgba(61,220,126,0.10), rgba(61,220,126,0.02))", border: "1px solid var(--green-border)" }}>
      <div className="flex items-center gap-4">
        <Flame size={20} style={{ color: "var(--green)" }} />
        <div>
          <div className="font-bold uppercase tracking-tight text-[14px]">Know another academy that'd love RollOSS?</div>
          <div className="label-tiny mt-1">Founding academies — first 10 only get 50% off, locked in for 12 months.</div>
        </div>
      </div>
      <button onClick={() => setOpen(true)} className="btn btn-outline shrink-0" data-testid="btn-refer-academy">Refer an academy</button>
      {open && <ReferAcademyModal academyName={user?.academy_name} onClose={() => setOpen(false)} />}
    </div>
  );
}

function KPI({ label, value, sub, color }) {
  return (
    <div className="card kpi-card" data-testid={`kpi-${label.replace(/\s+/g,'-').toLowerCase()}`}>
      <div className="label">{label}</div>
      <div className="kpi-value mt-3" style={color ? { color } : null}>{value}</div>
      {sub && <div className="label-tiny mt-2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [anns, setAnns] = useState([]);
  const [needs, setNeeds] = useState({ count: 0, students: [] });
  const [date, setDate] = useState(todayISO());
  const nav = useNavigate();

  useEffect(() => {
    const fetchAll = () => {
      api.get("/dashboard/summary", { params: { date } }).then(r => setData(r.data));
      api.get("/announcements").then(r => setAnns(r.data));
      api.get("/dashboard/needs_attention").then(r => setNeeds(r.data));
    };
    fetchAll();
    // Auto-refresh KPIs every 60s so the dashboard feels live as students check in
    const id = setInterval(fetchAll, 60000);
    return () => clearInterval(id);
  }, [date]);

  if (!data) return <div className="p-8 label">Loading…</div>;

  const isToday = date === todayISO();
  const dateLabel = formatDateLabel(date).toUpperCase();
  const max7 = Math.max(...data.attendance_7d, 1);
  const firstName = (data.user_name || "Coach").split(" ")[0];

  return (
    <div data-testid="page-dashboard">
      <PageHeader
        section={`${isToday ? "TODAY · " : ""}${dateLabel}`}
        title={`Welcome back, ${firstName}.`}
        right={
          <div className="flex items-center gap-3">
            <DateNav date={date} onChange={setDate} testid="dashboard-datenav" />
            <button className="btn btn-primary" onClick={()=>nav("/app/announcements")} data-testid="btn-new-announcement"><Plus size={14}/>New announcement</button>
          </div>
        }
      />
      <div className="p-8 space-y-6">
        <SetupWizard />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPI label="Active students" value={data.active_students} sub={data.active_delta ? `+${data.active_delta} this month` : "No new signups yet"} />
          <KPI label={isToday ? "Today's classes" : "Classes this day"} value={data.todays_classes} sub={`${data.sold_out_today} sold out`} />
          <KPI label={isToday ? "Check-ins · today" : "Check-ins"} value={data.checkins_today} sub={`${data.checkins_pct}% of members`} />
          <KPI label="Retention · 30d" value={data.retention_30d == null ? "—" : `${data.retention_30d}%`} sub={`Industry ${data.industry_retention}%`} color={data.retention_30d == null ? "var(--text-dim)" : "var(--green)"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card p-6 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div className="label">Today's classes</div>
              <div className="flex items-center gap-3">
                <span className="label-tiny" style={{ color: "var(--text-dim)" }}>Reserved / Capacity</span>
                <button className="label-tiny hover:text-white" onClick={()=>nav("/app/classes")} data-testid="link-view-schedule">View schedule →</button>
              </div>
            </div>
            <div className="mt-4 divide-y" style={{ borderColor: "var(--border)" }}>
              {data.todays_classes_list.length === 0 && <div className="py-6 text-sm" style={{ color: "var(--text-dim)" }}>No classes scheduled today.</div>}
              {data.todays_classes_list.map(c => (
                <div key={c.class_id}
                     className="py-4 flex items-center gap-5 cursor-pointer hover:opacity-90 transition-opacity"
                     onClick={() => nav(`/app/attendance?class_id=${c.class_id}&date=${date}`)}
                     data-testid={`today-class-${c.class_id}`}>
                  <div className="mono text-[13px]" style={{ color: "var(--text-dim)", width: 50 }}>{c.start_time}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-[15px] uppercase tracking-tight">{c.name}</div>
                    <div className="label-tiny mt-0.5">{c.instructor}</div>
                  </div>
                  <div className="mono text-sm font-semibold text-right" title="Students who reserved a spot in advance"
                       style={{ color: c.enrolled >= c.capacity ? "var(--green)" : "var(--text)", minWidth: 78 }}>
                    <span className="label-tiny block" style={{ fontSize: 10 }}>Reserved</span>
                    {c.enrolled}/{c.capacity}
                  </div>
                  <button className="btn btn-outline"
                          style={{ padding: "6px 14px", fontSize: 11 }}
                          onClick={(e) => { e.stopPropagation(); nav(`/app/attendance?class_id=${c.class_id}&date=${date}`); }}
                          data-testid={`btn-roster-${c.class_id}`}>Roster</button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="card p-6">
              <div className="label">Attendance · 7d</div>
              <div className="flex items-end gap-2 h-32 mt-5">
                {data.attendance_7d.map((v,i,a) => {
                  const daysAgo = a.length - 1 - i;
                  const barDate = new Date(Date.now() - daysAgo * 86400000).toISOString().slice(0,10);
                  return (
                    <div key={barDate} className="bar flex-1 rounded-t-md" style={{ height: `${(v/max7)*100}%`, background: i===a.length-1 ? "var(--green)" : "var(--border-strong)" }} />
                  );
                })}
              </div>
              <div className="flex justify-between mt-2"><span className="label-tiny">Mon</span><span className="label-tiny">Sun</span></div>
            </div>
            <div className="card p-6">
              <div className="label mb-4">Quick actions</div>
              <div className="grid grid-cols-2 gap-3">
                <button className="card card-hover p-4 text-left" onClick={()=>nav("/app/classes")} data-testid="qa-add-class">
                  <Plus size={16} style={{ color:"var(--green)" }}/>
                  <div className="mt-2 text-[13px] font-semibold uppercase tracking-tight">Add class</div>
                </button>
                <button className="card card-hover p-4 text-left" onClick={()=>nav("/app/students")} data-testid="qa-add-student">
                  <UserPlus size={16} style={{ color:"var(--green)" }}/>
                  <div className="mt-2 text-[13px] font-semibold uppercase tracking-tight">Add student</div>
                </button>
                <button className="card card-hover p-4 text-left" onClick={()=>nav("/app/announcements")} data-testid="qa-broadcast">
                  <Send size={16} style={{ color:"var(--green)" }}/>
                  <div className="mt-2 text-[13px] font-semibold uppercase tracking-tight">Broadcast</div>
                </button>
                <button className="card card-hover p-4 text-left" onClick={()=>nav("/app/attendance")} data-testid="qa-checkin">
                  <ScanLine size={16} style={{ color:"var(--green)" }}/>
                  <div className="mt-2 text-[13px] font-semibold uppercase tracking-tight">Check-in code</div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-6">
            <div className="label mb-4">Recent announcements</div>
            <div className="space-y-3">
              {anns.slice(0,3).map(a => (
                <div key={a.ann_id} className="flex items-start gap-3" data-testid={`ann-${a.ann_id}`}>
                  <span style={{ width:6, height:6, borderRadius:999, background:"var(--green)", marginTop:8 }} />
                  <div>
                    <div className="font-semibold text-[14px] uppercase tracking-tight">{a.title}</div>
                    <div className="label-tiny mt-1">{a.message.slice(0,72)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div className="label">Needs attention</div>
              <button className="label-tiny hover:text-white" onClick={()=>nav("/app/analytics")} data-testid="link-view-list">View list →</button>
            </div>
            <div className="mt-4 text-[15px]" data-testid="needs-attention-text">
              <span className="font-semibold">{needs.count}</span> students haven't trained in 14 days
            </div>
            <div className="mt-3 space-y-2">
              {needs.students.slice(0,3).map(s => (
                <div key={s.student_id} className="flex items-center justify-between text-[13px]">
                  <span style={{ color: "var(--text-dim)" }}>{s.name}</span>
                  <span className="label-tiny">{s.last_seen}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <ReferAcademyBanner />
      </div>
    </div>
  );
}
